<template>
  <div class="bg-[#f9f9f9]">
    <!-- Banner -->
    <div
      class="overflow-hidden flex flex-col items-center justify-center max-w-screen h-[300px] bg-cover bg-center relative text-white lg:pt-32"
      :style="{ backgroundImage: `url(${bgImage})` }"
    >
      <h2
        class="lg:text-[60px] text-[40px] font-semibold tracking-wider text-white"
      >
        Marketplace
      </h2>
      <p
        class="lg:text-[20px] text-[13px] font-light text-gray-200 mt-2 tracking-wide hidden"
      >
        Drive your <span class="font-extrabold text-white">Future</span>,
        <span class="font-bold text-white">Rent</span> with Ease.
        <span class="font-bold text-white">FutureRent</span>
      </p>
    </div>

    <!-- Layout -->
    <div class="flex flex-col lg:flex-row px-4 lg:px-20 py-10 gap-6">
      <!-- Mobile Filter Button -->
      <div class="block lg:hidden">
        <button
          @click="showFilters = !showFilters"
          class="w-full bg-white border border-[#CED4E7] text-[#0b1734] font-medium py-2 px-4 rounded-md mb-4"
        >
          {{ showFilters ? "Hide Filters" : "Show Filters" }}
        </button>

        <!-- Mobile Filter - Full screen overlay for mobile, slide-in panel for tablets -->
        <transition name="slide-fade">
          <aside
            v-if="showFilters"
            class="w-full bg-white p-4 rounded-md shadow-md fixed inset-0 z-50 overflow-y-auto md:max-w-md md:right-auto md:left-0 md:top-20 md:bottom-auto md:rounded-t-none md:rounded-b-lg"
          >
            <!-- Close button - positioned differently for mobile vs tablet -->
            <div
              class="flex justify-between items-center mb-4 sticky top-0 bg-white py-2"
            >
              <h3 class="font-semibold text-lg text-[#0b1734]">Filters</h3>
              <button
                @click="showFilters = false"
                aria-label="Close filters"
                class="text-[#0b1734] hover:text-gray-600 transition text-2xl"
              >
                &times;
              </button>
            </div>

            <!-- Filter Accordions -->
            <div class="space-y-4">
              <div
                v-for="(options, key) in vehicleFilters"
                :key="key"
                class="border-b border-gray-200 pb-4"
              >
                <button
                  @click="toggleMobileFilter(key)"
                  class="w-full flex justify-between items-center text-left"
                >
                  <h4 class="text-sm font-semibold capitalize text-[#0b1734]">
                    {{ key.replaceAll("_", " ") }} ({{
                      selectedFilters[key]?.length || 0
                    }})
                  </h4>
                  <svg
                    :class="{
                      'transform rotate-180': activeMobileFilter === key,
                    }"
                    class="w-5 h-5 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <transition name="slide-fade">
                  <div v-show="activeMobileFilter === key" class="mt-2 pl-2">
                    <div class="grid grid-cols-2 gap-2 text-sm text-[#0b1734]">
                      <label
                        v-for="option in options"
                        :key="option"
                        class="flex items-center capitalize"
                      >
                        <input
                          type="checkbox"
                          class="mr-2 rounded border-gray-300 text-[#0b1734] focus:ring-[#0b1734]"
                          :value="option"
                          v-model="selectedFilters[key]"
                        />
                        {{ option }}
                      </label>
                    </div>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Price Filter - Always visible -->
            <div class="mt-6 pt-4 border-t border-gray-200">
              <div class="flex justify-between items-center mb-2">
                <h4 class="text-sm font-semibold text-[#0b1734]">Max Price</h4>
                <span class="font-medium text-sm"
                  >R{{ maxPrice.toLocaleString() }}</span
                >
              </div>
              <input
                type="range"
                min="100"
                max="20000"
                step="100"
                v-model="maxPrice"
                class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div class="flex justify-between text-xs text-gray-500 mt-1">
                <span>R100</span>
                <span>R20,000</span>
              </div>
            </div>

            <!-- Apply/Clear buttons for mobile -->
            <div class="flex gap-3 mt-6 sticky bottom-0 bg-white py-3">
              <button
                @click="clearAllFilters"
                class="flex-1 py-2 border border-[#CED4E7] rounded-md text-[#0b1734] font-medium"
              >
                Clear All
              </button>
              <button
                @click="showFilters = false"
                class="flex-1 py-2 bg-[#0b1734] rounded-md text-white font-medium"
              >
                Apply Filters
              </button>
            </div>
          </aside>
        </transition>
      </div>

      <!-- Sidebar Filter (Desktop Only) -->
      <aside
        class="hidden lg:block w-[300px] bg-white p-7 rounded-md shadow-md h-fit sticky top-24"
      >
        <h3 class="font-semibold text-lg mb-4 text-[#0b1734]">Filters</h3>

        <div v-for="(options, key) in vehicleFilters" :key="key" class="mb-5">
          <button
            @click="toggleDropdown(key)"
            class="w-full text-left text-sm font-semibold text-[#0b1734] mb-2 flex justify-between items-center"
          >
            <span class="capitalize">{{ key.replaceAll("_", " ") }}</span>
            <span>
              {{ openFilterKey === key ? "−" : "+" }}
            </span>
          </button>
          <transition name="slide-fade">
            <div
              v-show="openFilterKey === key"
              class="grid grid-cols-2 gap-2 text-[12px] text-[#0b1734]"
            >
              <label
                v-for="option in options"
                :key="option"
                class="flex items-center capitalize"
              >
                <input
                  type="checkbox"
                  class="mr-2"
                  :value="option"
                  v-model="selectedFilters[key]"
                />
                {{ option }}
              </label>
            </div>
          </transition>
        </div>

        <!-- Price Filter -->
        <div class="mt-6">
          <h4 class="text-sm font-semibold mb-2 text-[#0b1734]">Max Price</h4>
          <div class="flex justify-between text-xs text-gray-600 mb-2">
            <span>Up to</span>
            <span class="font-medium">R{{ maxPrice.toLocaleString() }}</span>
          </div>
          <input
            type="range"
            min="100"
            max="20000"
            step="100"
            v-model="maxPrice"
            class="w-full"
          />
        </div>

        <!-- Clear Button (Desktop) -->
        <div class="mt-6">
          <button
            @click="clearAllFilters"
            class="w-full py-2 border border-[#CED4E7] rounded-md text-[#0b1734] font-medium hover:bg-gray-100 transition"
          >
            Clear All
          </button>
        </div>
      </aside>

      <!-- Main Content -->
      <section ref="contentSection" class="flex-1 space-y-6 w-full">
        <!-- Search Bar -->
        <div
          class="lg:sticky relative top-0 lg:top-[77px] z-0 lg:z-30 bg-[#f9f9f9] pt-4 pb-2"
        >
          <!-- Search Bar and Sort Dropdown -->
          <div
            class="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-2 sm:px-0"
          >
            <!-- Search Input -->
            <div class="w-full sm:max-w-sm">
              <transition name="fade-slide" mode="out-in">
                <div
                  v-if="showSearch || isMobile"
                  key="search-input"
                  class="flex items-center gap-2 border border-[#CED4E7] px-4 py-2 rounded-md w-full bg-white"
                >
                  <PhMagnifyingGlass size="20" class="text-[#0b1734]" />
                  <input
                    type="text"
                    placeholder="Search Vehicle..."
                    v-model="searchQuery"
                    class="flex-1 bg-transparent outline-none text-sm text-[#0b1734] placeholder:text-gray-400"
                    @keyup.enter="showSearch = false"
                    autofocus
                  />
                  <button
                    @click="clearSearch"
                    class="text-[#0b1734] hover:text-gray-600 transition"
                    aria-label="Close search"
                  >
                    <PhX size="20" />
                  </button>
                </div>
                <div v-else key="search-icon" class="inline-block">
                  <button
                    @click="showSearch = true"
                    class="text-[#0b1734] hover:text-gray-600 transition-all"
                    aria-label="Open search"
                  >
                    <PhMagnifyingGlass size="24" />
                  </button>
                </div>
              </transition>
            </div>

            <!-- Sort Dropdown -->
            <div class="relative w-full sm:w-auto">
              <button
                @click="sortDropdownOpen = !sortDropdownOpen"
                class="w-full sm:w-auto bg-white border border-[#CED4E7] text-[#0b1734] px-4 py-2 rounded-md flex justify-between sm:justify-center items-center gap-2"
              >
                Sort:
                <span class="font-semibold capitalize">{{ sortOrder }}</span>
                <svg
                  :class="{ 'transform rotate-180': sortDropdownOpen }"
                  class="w-4 h-4 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <transition name="fade">
                <ul
                  v-if="sortDropdownOpen"
                  class="absolute z-50 mt-1 w-full sm:w-32 bg-white border border-gray-200 rounded-md shadow-lg"
                >
                  <li>
                    <button
                      @click="setSortOrder('asc')"
                      class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      :class="{ 'font-semibold': sortOrder === 'asc' }"
                    >
                      Ascending
                    </button>
                  </li>
                  <li>
                    <button
                      @click="setSortOrder('desc')"
                      class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      :class="{ 'font-semibold': sortOrder === 'desc' }"
                    >
                      Descending
                    </button>
                  </li>
                </ul>
              </transition>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-10">
          <p class="text-gray-500">Loading vehicles...</p>
        </div>

        <!-- Vehicle List -->
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 sm:px-0"
        >
          <div
            v-for="vehicle in paginatedVehicles"
            :key="vehicle.id"
            class="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-gray-200 block cursor-pointer"
            @click="handleRedirect(vehicle)"
          >
            <div
              class="rounded-lg mb-4 overflow-hidden lg:h-fit flex items-center justify-center relative h-48"
            >
              <img
                v-if="vehicle.image"
                :src="vehicle.image"
                :alt="vehicle.carname"
                class="w-full h-full object-contain"
              />
              <div
                v-else
                class="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400"
              >
                No Image Available
              </div>
            </div>
          </div>

          <!-- No Vehicles Found -->
          <div
            v-if="
              searchQuery !== '' && filteredVehicles.length === 0 && !loading
            "
            class="col-span-full text-center py-10"
          >
            <p class="text-gray-500">No vehicles found matching your search.</p>
          </div>
          <div
            v-if="
              searchQuery === '' && filteredVehicles.length === 0 && !loading
            "
            class="col-span-full text-center py-10"
          >
            <p class="text-gray-500">No vehicles found</p>
          </div>
        </div>

        <!-- Pagination buttons -->
        <div
          v-if="!loading && filteredVehicles.length > 0"
          class="flex flex-wrap justify-center mt-10 gap-2 px-2 sm:px-0"
        >
          <button
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-1 rounded-md text-sm font-medium border',
              page === pageNo
                ? 'bg-[#0b1734] text-white border-[#0b1734]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100',
            ]"
          >
            {{ page }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { PhMagnifyingGlass, PhX } from "@phosphor-icons/vue";
import { fetchMarketPlace } from "@/reusable/fetchMarketPlace";
import bgImage from "@/assets/images/showroom.png";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

const store = useStore();
const router = useRouter();

const searchQuery = ref("");
const showSearch = ref(false);
const showFilters = ref(false);
const isMobile = ref(false);
const openFilterKey = ref(null); // desktop accordion state
const activeMobileFilter = ref(null); // mobile accordion state
const maxPrice = ref(20000);
const contentSection = ref(null);
const sortOrder = ref("asc");
const pageNo = ref(1);
const sortDropdownOpen = ref(false);
const pageSize = ref(6);
const marketplaceData = ref(null);
const loading = ref(true);
const vehicles = ref([]);

const vehicleFilters = ref({});
const selectedFilters = ref({});

const toggleMobileFilter = (key) => {
  activeMobileFilter.value = activeMobileFilter.value === key ? null : key;
};

const toggleDropdown = (key) => {
  openFilterKey.value = openFilterKey.value === key ? null : key;
};

const clearAllFilters = () => {
  for (const key in selectedFilters.value) {
    selectedFilters.value[key] = [];
  }
  maxPrice.value = 20000;
};

onMounted(async () => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
  };

  try {
    const data = await fetchMarketPlace();
    marketplaceData.value = data;

    // Initialize filters from enums
    const filters = {
      make: data.enums.make,
      body_type: data.enums.body_type,
      transmission: data.enums.transmission,
      drive_train: data.enums.drive_train,
    };
    vehicleFilters.value = filters;

    // Initialize selectedFilters keys
    for (const key in filters) {
      selectedFilters.value[key] = [];
    }

    // Populate selectedFilters from URL params
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (key in selectedFilters.value) {
        selectedFilters.value[key] = value.split(",");
      } else if (key === "max_price") {
        maxPrice.value = Number(value);
      } else if (key === "search") {
        searchQuery.value = value;
      } else if (key === "sortOrder") {
        sortOrder.value = value;
      } else if (key === "page") {
        pageNo.value = Number(value);
      }
    }

    // Process vehicle data
    vehicles.value = data.data
      .filter((item) => item.isDeleted === "0")
      .map((item) => ({
        id: item.id,
        carname: `${item.make} ${item.model}`,
        image: item.image_url,
        specs: {
          brand: item.make,
          model: item.model,
          year: item.year,
          color: item.colours ? JSON.parse(item.colours)[0] : "Unknown",
          transmission: item.transmission,
          price: parseFloat(item.rental),
          body_type: item.body_type,
          drive_train: item.drive_train,
          fuel_consumption: item.fuel_consumption,
          insurance: parseFloat(item.insurance),
          tracker: parseFloat(item.tracker),
          initiation_fee: parseFloat(item.initiation_fee || 0),
        },
      }));
  } catch (error) {
    console.error("Error fetching marketplace data:", error);
  } finally {
    loading.value = false;
  }

  checkMobile();
  window.addEventListener("resize", checkMobile);
});

const handleRedirect = (vehicle) => {
  store.commit("SET_VEHICLE_ID", vehicle.id);
  router.push({
    name: "VehicleDetails",
    params: {
      carname: `${vehicle.specs.brand}_${vehicle.specs.model}_${vehicle.specs.year}`,
    },
  });
};

const filteredVehicles = computed(() => {
  if (loading.value) return [];

  let results = [...vehicles.value];

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    results = results.filter(
      (v) =>
        v.carname.toLowerCase().includes(query) ||
        v.specs.brand.toLowerCase().includes(query) ||
        v.specs.model.toLowerCase().includes(query)
    );
  }

  // Price filter
  results = results.filter((v) => v.specs.price <= maxPrice.value);

  // Other filters
  for (const [filterKey, selectedValues] of Object.entries(
    selectedFilters.value
  )) {
    if (selectedValues.length > 0) {
      const propPath = {
        make: "specs.brand",
        model: "specs.model",
        body_type: "specs.body_type",
        transmission: "specs.transmission",
        drive_train: "specs.drive_train",
      }[filterKey];

      if (!propPath) continue;

      results = results.filter((v) => {
        const value = propPath.split(".").reduce((obj, key) => obj[key], v);
        return selectedValues.includes(value);
      });
    }
  }

  // Sorting
  results.sort((a, b) =>
    sortOrder.value === "asc"
      ? a.specs.price - b.specs.price
      : b.specs.price - a.specs.price
  );

  return results;
});

const totalPages = computed(() =>
  Math.ceil(filteredVehicles.value.length / pageSize.value)
);

const paginatedVehicles = computed(() => {
  const start = (pageNo.value - 1) * pageSize.value;
  return filteredVehicles.value.slice(start, start + pageSize.value);
});

const clearSearch = () => {
  searchQuery.value = "";
  showSearch.value = false;
};

const goToPage = async (page) => {
  if (page < 1 || page > totalPages.value) return;
  pageNo.value = page;
  await nextTick();
  contentSection.value?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const setSortOrder = (order) => {
  if (sortOrder.value !== order) {
    sortOrder.value = order;
    sortDropdownOpen.value = false;
  }
};

watch(searchQuery, (newQuery) => {
  searchQuery.value = newQuery.trim();
});

watch(
  [selectedFilters, maxPrice, searchQuery, sortDropdownOpen, pageNo],
  async () => {
    const activeFilters = {};
    for (const key in selectedFilters.value) {
      if (selectedFilters.value[key].length) {
        activeFilters[key] = selectedFilters.value[key];
      }
    }
    if (maxPrice.value < 20000) activeFilters.max_price = maxPrice.value;
    if (searchQuery.value) activeFilters.search = searchQuery.value;
    if (sortOrder.value) {
      activeFilters.sortOrder = sortOrder.value;
      activeFilters.sortBy = "price";
    }
    if (pageNo.value) activeFilters.page = pageNo.value;

    const params = new URLSearchParams();
    for (const [key, values] of Object.entries(activeFilters)) {
      if (Array.isArray(values)) values.forEach((v) => params.append(key, v));
      else params.append(key, values);
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  },
  { deep: true }
);
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.95);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.95);
}

.grid {
  margin-top: 1rem;
}
.bg-white {
  background-color: #fff;
}
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
.rounded-lg {
  border-radius: 0.5rem;
}
.transition-all {
  transition: all 0.3s ease-in-out;
}
.block:hover h4 {
  text-decoration: underline;
}

/* Custom scrollbar for filters */
aside::-webkit-scrollbar {
  width: 6px;
}
aside::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
aside::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}
aside::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Range slider styling */
input[type="range"] {
  -webkit-appearance: none;
  height: 6px;
  background: #e0e0e0;
  border-radius: 5px;
  background-image: linear-gradient(#0b1734, #0b1734);
  background-size: 0% 100%;
  background-repeat: no-repeat;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #0b1734;
  cursor: pointer;
  box-shadow: 0 0 2px 0 #555;
}

input[type="range"]::-moz-range-thumb {
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #0b1734;
  cursor: pointer;
  box-shadow: 0 0 2px 0 #555;
}

input[type="range"]::-ms-thumb {
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #0b1734;
  cursor: pointer;
  box-shadow: 0 0 2px 0 #555;
}

/* For mobile overlay */
@media (max-width: 767px) {
  aside {
    height: 100vh;
    border-radius: 0;
  }
}

/* For tablet (md) devices */
@media (min-width: 768px) and (max-width: 1023px) {
  aside {
    max-height: 80vh;
    top: 80px;
    left: 20px;
    width: 350px;
  }
}

/* Better touch targets for mobile */
label {
  padding: 8px 0;
}
input[type="checkbox"] {
  min-width: 18px;
  min-height: 18px;
}
</style>
